import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconChart, IconEdit, IconReceipt } from '../../../common/icons';
import { formatDateOnly } from '../../../common/utils/date';
import { useAnimal } from '../contexts/animals';
import StaysPanel from '../../billing/forms/stays-panel';
import MedicalEventsPanel from '../../medical/forms/events-panel';
import GenealogyTree from '../../genealogy/layouts/tree';
import DonorPregnanciesPanel from '../../pregnancies/layouts/donor-panel';
import DonorAbortionsPanel from '../../pregnancies/layouts/donor-abortions-panel';
import DonorOffspringPanel from './offspring-panel';
import AnimalValuationsPanel from './valuations-panel';
import AnimalLocationPanel from './location-panel';
import DonorOpuBreakdownPanel from './opu-breakdown-panel';

import '../../../common/styles/catalog.css';
import './styles/detail-tabs.css';

const STATUS_LABEL = { 2: 'Activo', 1: 'Inactivo', 0: 'Eliminado' };
const SEX_LABEL = { female: 'Hembra', male: 'Macho' };

function Field({ label, value }) {
    return (
        <div className="cat-detail__field">
            <span className="cat-detail__field-label">{label}</span>
            <span className="cat-detail__field-value">
                {value || <span className="cat-detail__empty">—</span>}
            </span>
        </div>
    );
}

export default function AnimalDetail({ animalId }) {
    const { data: animal, isLoading, error } = useAnimal(animalId);
    const [tab, setTab] = useState('info');

    if (isLoading) return <div className="cat-detail__state">Cargando animal…</div>;
    if (error || !animal) return <div className="cat-detail__state">Animal no encontrado.</div>;

    const title = animal.name || animal.number || `Animal #${animal.id}`;
    const isFemale = animal.sex === 'female';
    const tabs = [
        { id: 'info', label: 'Información' },
        { id: 'salud', label: 'Salud' },
        { id: 'financiero', label: 'Financiero' },
        ...(isFemale ? [{ id: 'reproduccion', label: 'Reproducción' }] : []),
    ];

    return (
        <div className="cat-detail">
            <header className="cat-detail__header">
                <div>
                    <Link to={isFemale ? '/admin/animals' : '/admin/toros'} className="cat-detail__back">{isFemale ? 'Donadoras' : 'Toros'}</Link>
                    <div className="cat-detail__title-row">
                        <h1 className="cat-detail__title">{title}</h1>
                        <span className={`cat-badge cat-badge--${animal.status}`}>{STATUS_LABEL[animal.status] || '—'}</span>
                        {animal.baptized === false && <span className="cat-badge cat-badge--1">Sin bautizar</span>}
                    </div>
                </div>
                <div className="cat-detail__actions">
                    <Gate domain="billing" action="read">
                        <Link to={`/admin/animals/${animal.id}/liquidation`} className="cat-detail__edit">
                            <IconReceipt /><span>Liquidar</span>
                        </Link>
                    </Gate>
                    {isFemale && (
                        <Gate domain="kpis" action="read">
                            <Link to={`/admin/animals/${animal.id}/stats`} className="cat-detail__edit">
                                <IconChart /><span>Estadísticas</span>
                            </Link>
                        </Gate>
                    )}
                    <Gate domain="animals" action="write">
                        <Link to={`/admin/animals/${animal.id}/edit`} className="cat-detail__edit">
                            <IconEdit /><span>Editar</span>
                        </Link>
                    </Gate>
                </div>
            </header>

            <nav className="atabs" aria-label="Secciones">
                {tabs.map((t) => (
                    <button key={t.id} type="button" className={`atab ${tab === t.id ? 'atab--active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>
                ))}
            </nav>

            {/* Información: info + ubicación + genealogía */}
            {tab === 'info' && (
                <>
                    <section className="cat-detail__card">
                        <h2 className="cat-detail__section-title">Información</h2>
                        <div className="cat-detail__grid">
                            <Field label="Número / arete" value={animal.number} />
                            <Field label="Registro" value={animal.register} />
                            <Field label="Raza" value={animal.race?.name} />
                            <Field label="Sexo" value={SEX_LABEL[animal.sex]} />
                            <Field label="Nacimiento" value={formatDateOnly(animal.birth)} />
                            <Field label="Apariencia" value={animal.appearance} />
                        </div>
                        {animal.description && (
                            <div className="cat-detail__notes">
                                <span className="cat-detail__field-label">Descripción</span>
                                <p>{animal.description}</p>
                            </div>
                        )}
                    </section>

                    <Gate domain="animals" action="read">
                        <AnimalLocationPanel animalId={animal.id} />
                    </Gate>

                    <Gate domain="genealogies" action="read">
                        <GenealogyTree animalId={animal.id} />
                    </Gate>
                </>
            )}

            {/* Salud: eventos médicos */}
            {tab === 'salud' && (
                <Gate domain="medical_events" action="read">
                    <MedicalEventsPanel animalId={animal.id} />
                </Gate>
            )}

            {/* Financiero: valorización + manejos (estadías) */}
            {tab === 'financiero' && (
                <>
                    <Gate domain="kpis" action="read">
                        <AnimalValuationsPanel animal={animal} />
                    </Gate>
                    <Gate domain="billing" action="read">
                        <StaysPanel animalId={animal.id} />
                    </Gate>
                </>
            )}

            {/* Reproducción: desglose OPU + preñeces + pérdidas + crías (solo hembras) */}
            {tab === 'reproduccion' && isFemale && (
                <>
                    <Gate domain="kpis" action="read">
                        <DonorOpuBreakdownPanel animalId={animal.id} />
                    </Gate>
                    <Gate domain="pregnancies" action="read">
                        <DonorPregnanciesPanel animalId={animal.id} />
                    </Gate>
                    <Gate domain="pregnancies" action="read">
                        <DonorAbortionsPanel animalId={animal.id} />
                    </Gate>
                    <Gate domain="kpis" action="read">
                        <DonorOffspringPanel animalId={animal.id} />
                    </Gate>
                </>
            )}
        </div>
    );
}
